import React from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { User } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isVipSubscriber } from '../utils/isVipSubscriber';
import { getVipDaysRemaining } from '../utils/getVipDaysRemaining';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import './UserNavbarMenus.css';

export interface UserNavbarMenusProps {
  user: User;
  pendingRequestsCount: number;
  /** In-app notification badge; refreshed with profile load (no dedicated polling). */
  unreadNotificationCount: number;
  onUnreadNotificationCountChange: (count: number) => void;
  onOpenFriends: () => void;
  onOpenPending: () => void;
  onLogout: () => void;
}

/**
 * Desktop: user NavDropdown. Mobile: same entries as flat Nav links (no extra tap on user name).
 */
const UserNavbarMenus: React.FC<UserNavbarMenusProps> = ({
  user,
  pendingRequestsCount,
  unreadNotificationCount,
  onUnreadNotificationCountChange,
  onOpenFriends,
  onOpenPending,
  onLogout,
}) => {
  const { t, i18n } = useSafeTranslation();
  const displayName = user.nickname || user.name;
  const vipDays = getVipDaysRemaining(user.vipExpiresAtUtc);
  const vipMenuText = (() => {
    if (!isVipSubscriber(user.userType)) {
      return t('vip.menuLabelBecome', 'Become VIP');
    }
    if (vipDays !== null) {
      return i18n.t('vip.menuLabelWithDays', { count: vipDays });
    }
    return t('vip.menuLabel', 'VIP');
  })();

  const dropdownTitle = (
    <span>
      <i className="bi bi-person-circle me-2"></i>
      {displayName}
    </span>
  );

  return (
    <>
      <div className="d-none d-lg-flex align-items-center me-1 gap-1">
        <NavbarMusicToggle />
        <NotificationBell
          unreadCount={unreadNotificationCount}
          onUnreadCountChange={onUnreadNotificationCountChange}
        />
      </div>
      <div className="d-none d-lg-block">
        <NavDropdown
          title={dropdownTitle}
          id="user-dropdown"
          className="user-dropdown"
          align="end"
        >
          <NavDropdown.Item as={Link} to="/profile">
            <i className="bi bi-person me-2"></i>
            {t('common.profile', 'My Profile')}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/inventory">
            <i className="bi bi-bag me-2"></i>
            {t('navigation.inventory', 'Inventory')}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/notifications">
            <i className="bi bi-bell me-2"></i>
            {t('notifications.menuItem', 'Notifications')}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/leaderboard">
            <i className="bi bi-bar-chart-line me-2"></i>
            {t('ranking.highscores', 'Highscores')}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/achievements/catalog">
            <i className="bi bi-trophy me-2"></i>
            {t('achievements.catalogMenuLabel', 'Achievements')}
          </NavDropdown.Item>
          <NavDropdown.Item
            onClick={() => {
              onOpenFriends();
            }}
          >
            <i className="bi bi-people me-2"></i>
            {t('friends.friendsList', 'My Friends')}
          </NavDropdown.Item>
          <NavDropdown.Item
            onClick={() => {
              onOpenPending();
            }}
          >
            <i className="bi bi-envelope me-2"></i>
            {t('friends.pendingRequests', 'Friend Requests')}
            {pendingRequestsCount > 0 && (
              <span className="badge bg-danger ms-2">{pendingRequestsCount}</span>
            )}
          </NavDropdown.Item>
          {(user.userType === 'Admin' || user.userType === 3) && (
            <>
              <NavDropdown.Item as={Link} to="/admin">
                <i className="bi bi-shield-lock me-2"></i>
                {t('admin.adminPanel', 'Admin Panel')}
              </NavDropdown.Item>
              <NavDropdown.Divider />
            </>
          )}
          <div className="vip-menu-entry">
            <NavDropdown.Item as={Link} to="/vip" className="vip-menu-entry__link dropdown-item">
              <span className="vip-menu-entry__diamond" aria-hidden>
                💎
              </span>
              {vipMenuText}
            </NavDropdown.Item>
          </div>
          <NavDropdown.Item as={Link} to="/contact">
            <i className="bi bi-chat-dots me-2"></i>
            {t('footer.contact', 'Contact')}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/settings">
            <i className="bi bi-gear me-2"></i>
            {t('common.settings', 'Settings')}
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item onClick={onLogout}>
            <i className="bi bi-box-arrow-right me-2"></i>
            {t('common.logout', 'Logout')}
          </NavDropdown.Item>
        </NavDropdown>
      </div>

      <div className="d-lg-none w-100 border-top mt-2 pt-2 navbar-user-menu-mobile">
        <Nav className="flex-column pt-1">
          <Nav.Link as={Link} to="/profile" className="text-dark py-2 rounded">
            <i className="bi bi-person me-2"></i>
            {t('common.profile', 'My Profile')}
          </Nav.Link>
          <Nav.Link as={Link} to="/inventory" className="text-dark py-2 rounded">
            <i className="bi bi-bag me-2"></i>
            {t('navigation.inventory', 'Inventory')}
          </Nav.Link>
          <Nav.Link as={Link} to="/notifications" className="text-dark py-2 rounded">
            <i className="bi bi-bell me-2"></i>
            {t('notifications.menuItem', 'Notifications')}
          </Nav.Link>
          <Nav.Link as={Link} to="/leaderboard" className="text-dark py-2 rounded">
            <i className="bi bi-bar-chart-line me-2"></i>
            {t('ranking.highscores', 'Highscores')}
          </Nav.Link>
          <Nav.Link as={Link} to="/achievements/catalog" className="text-dark py-2 rounded">
            <i className="bi bi-trophy me-2"></i>
            {t('achievements.catalogMenuLabel', 'Achievements')}
          </Nav.Link>
          <Nav.Link
            className="text-dark py-2 rounded"
            onClick={() => {
              onOpenFriends();
            }}
          >
            <i className="bi bi-people me-2"></i>
            {t('friends.friendsList', 'My Friends')}
          </Nav.Link>
          <Nav.Link
            className="text-dark py-2 rounded"
            onClick={() => {
              onOpenPending();
            }}
          >
            <i className="bi bi-envelope me-2"></i>
            {t('friends.pendingRequests', 'Friend Requests')}
            {pendingRequestsCount > 0 && (
              <span className="badge bg-danger ms-2">{pendingRequestsCount}</span>
            )}
          </Nav.Link>
          {(user.userType === 'Admin' || user.userType === 3) && (
            <Nav.Link as={Link} to="/admin" className="text-dark py-2 rounded">
              <i className="bi bi-shield-lock me-2"></i>
              {t('admin.adminPanel', 'Admin Panel')}
            </Nav.Link>
          )}
          <div className="vip-menu-entry">
            <Nav.Link
              as={Link}
              to="/vip"
              className="vip-menu-entry__link text-dark py-2 rounded"
            >
              <span className="vip-menu-entry__diamond" aria-hidden>
                💎
              </span>
              {vipMenuText}
            </Nav.Link>
          </div>
          <Nav.Link as={Link} to="/contact" className="text-dark py-2 rounded">
            <i className="bi bi-chat-dots me-2"></i>
            {t('footer.contact', 'Contact')}
          </Nav.Link>
          <Nav.Link as={Link} to="/settings" className="text-dark py-2 rounded">
            <i className="bi bi-gear me-2"></i>
            {t('common.settings', 'Settings')}
          </Nav.Link>
          <Nav.Link className="text-dark py-2 rounded" onClick={onLogout}>
            <i className="bi bi-box-arrow-right me-2"></i>
            {t('common.logout', 'Logout')}
          </Nav.Link>
        </Nav>
      </div>
    </>
  );
};

export default UserNavbarMenus;
